import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ICustomerInfoResponse } from '../account/customerinforesponse';
import LogRocket from 'logrocket';

// LogRocket's typings are often CommonJS (export = LogRocket),
// so "default" may not exist. We'll normalize it safely.
type LogRocketApi = {
  init: (appId: string) => void;
  identify: (id: string, traits?: Record<string, any>) => void;
  shutdown?: () => void;
};

@Injectable({ providedIn: 'root' })
export class LogRocketService {
  private lr: LogRocketApi | null = null;
  private initialized = false;
  private identified = false;
  private loading: Promise<void> | null = null;

  private get enabled(): boolean {
    if (!(environment as any).logrocketEnabled) return false;
    if (typeof window === 'undefined') return false;

    // runtime kill switch
    return window.localStorage.getItem('disableLogRocket') !== 'true';
  }

  async init(): Promise<void> {
    if (!this.enabled || this.initialized) return;
    if (this.loading) return this.loading;

    this.loading = (async () => {
      const appId = (environment as any).logrocketAppId as string | undefined;
      if (!appId) {
        console.warn('[LogRocket] Disabled: missing appId');
        return;
      }

      const mod: any = await import('logrocket');
      // Normalize CommonJS vs ESM
      this.lr = (mod?.default ?? mod) as LogRocketApi;

      this.lr.init(appId);
      this.initialized = true;
    })();

    return this.loading;
  }

  async identifyCustomer(user: ICustomerInfoResponse, ooid?: any): Promise<void> {
    if (!this.enabled || this.identified || !user?.CustomerDID) return;

    await this.init();
    if (!this.lr) return;

    const id = user.CustomerDID.toString();

    this.lr.identify(id, {
      name: `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim(),
      email: user.Email?.trim() ?? `${id}@lbri.com`,
      app: 'FSS',
      domain: window.location.hostname,
      role: 'Customer',
      onlineOrderId: ooid,
    });

    this.identified = true;
  }

 // Call on logout / user switch
  shutdown(): void {
    this.identified = false;

    // If the SDK supports shutdown, call it
    if (this.lr && typeof this.lr.shutdown === 'function') {
      this.lr.shutdown();
    }

    // Allow re-init later if desired
    this.lr = null;
    this.initialized = false;
  }

  // Runtime toggle (does not touch environment files)
  setRuntimeEnabled(enabled: boolean): void {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem('disableLogRocket', enabled ? 'false' : 'true');

    if (!enabled) {
      this.shutdown();
    }
  }
}
